import ReactECharts from 'echarts-for-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { MeasurementMarkerDto, SessionOptionDto } from 'types/models/SessionsPageData';
import { TargetShapeGeo } from 'types/models/SessionEnrichment';
import { t } from 'utilities/TranslationUtils';
import { KICK_RESULT_BUCKET_COLORS, KICK_RESULT_Z, SCATTER_SYMBOL_SIZE, kickMarkerColor, measurementResultBucket, withAlpha } from 'utilities/chartUtils';

// Top-view kick dots are slim rings (translucent fill, solid outline in the result colour) a little larger than the
// shared default, so overlapping kicks stay readable on the pitch. All hover/glow/pulse offsets scale proportionally.
const PITCH_SYMBOL_SIZE = Math.round(SCATTER_SYMBOL_SIZE * 1.1);
const DOT_FILL_ALPHA = 0.62;
/** White ring + soft dark halo: keeps a dot readable on the green turf as well as on the light background. */
const DOT_RING = 'rgba(255,255,255,0.95)';
const DOT_HALO = 'rgba(0,0,0,0.45)';

import { useDragRectSelect, clampModalX, clampModalY } from './useDragRectSelect';
// Round 21: flip lateral Z so the Sessions charts match the live video + 3D conventions —
// same helper the MeasurementDetail page already uses, so all 2D drawings now agree.
import { soccerZToChartZ } from 'pages/MeasurementDetail/coords';
import {
  buildTopViewPitchOption,
  FULL_PITCH_LENGTH,
  CAMERA_GOAL_AREA_FOCUS,
  buildTargetSeries,
  goalAreaDepthWindow,
  landscapeWindow,
  transposeTopViewOption,
  PITCH_GREEN_STRIPE,
  PITCH_GREEN,
} from './topViewPitchOptions';

// Half-pitch view: focused on penalty box with 40% buffer above and below.
const VIEW_Z = 36.3;        // lateral half-width — pitch width (66) + 5% padding (3.3 on each side)

type Mode = 'origin' | 'landing';

type Props = {
  data: MeasurementMarkerDto[];
  sessions: SessionOptionDto[];
  selectedIds: ReadonlySet<string>;
  hoveredId: string | null;
  /** Called with the kick ID + clientX/clientY (round 17: also fires on hover so the parent can drive the info modal). */
  onPointClick: (id: string, clientX: number, clientY: number) => void;
  onPointHover: (id: string | null, clientX?: number, clientY?: number) => void;
  onDragSelected: (measurementIds: string[]) => void;
  mode: Mode;
  hasActiveFilters?: boolean;
  onClearFilters?: () => void;
  showSelectionState?: boolean;
  dragSelectionEnabled?: boolean;
  performanceMode?: boolean;
  clearKicks?: boolean;
  /** Pitch turned 90°: goal line on the left, kicks travelling right (see landscapeWindow). */
  landscape?: boolean;
  /** Session targets drawn flat on the pitch (the aimed-at one is brighter). */
  targets?: TargetShapeGeo[];
};

const SessionsPitchChart = ({
  data,
  sessions,
  selectedIds,
  hoveredId,
  onPointClick,
  onPointHover,
  onDragSelected,
  mode,
  hasActiveFilters,
  onClearFilters,
  showSelectionState = true,
  dragSelectionEnabled = true,
  performanceMode = false,
  clearKicks = false,
  landscape = false,
  targets,
}: Props): React.ReactElement => {
  const containerRef = useRef<HTMLDivElement>(null);
  const echartsRef = useRef<ReactECharts>(null);
  const lastMouseClient = useRef<{ clientX: number; clientY: number } | null>(null);
  const displayData = clearKicks ? [] : data;

  // A container taller than the default view shows more of the pitch (towards midfield) rather than stretching it.
  const [aspect, setAspect] = useState<number | null>(null);
  useEffect(() => {
    const box = containerRef.current;
    if (!box || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      if (width > 0 && height > 0) setAspect(Math.round((height / width) * 100) / 100);
    });
    observer.observe(box);
    return () => observer.disconnect();
  }, []);
  const landscapeView = landscape ? landscapeWindow(aspect ? 1 / aspect : null) : null;
  const { yMin: viewXMin, yMax: viewXMax } = landscapeView ?? goalAreaDepthWindow(aspect);
  const viewZ = landscapeView ? landscapeView.zHalf : VIEW_Z;

  const inPitchBounds = (z: number, x: number) => z >= -viewZ && z <= viewZ && x >= viewXMin && x <= viewXMax;

  // SDK snapshots can provide reachable-region points as [x,z], [z,x], or 3D [x,y,z].
  // Normalize defensively so corner overlays still render even when upstream point shape varies.
  // NOTE: duplicated with previewCoordinateMapping.ts (mapTopPreviewReachablePoint) — coordinate-normalization
  // logic overlaps but differs in bounds/output units; intentionally not merged (too risky to verify).
  const toPitchPoint = (coords: [number, number] | null, marker: MeasurementMarkerDto): [number, number] | null => {
    if (!coords || coords.length < 2) return null;

    const candidates: [number, number][] = [];

    // Assume [x,z]
    candidates.push([soccerZToChartZ(coords[1]), coords[0]]);
    // Alternate [z,x]
    candidates.push([soccerZToChartZ(coords[0]), coords[1]]);

    const inBounds = candidates.find((c) => inPitchBounds(c[0], c[1]));
    if (inBounds) return inBounds;

    // If none are in the current viewport, prefer the one nearest to kick origin.
    if (marker.kickX !== null && marker.kickZ !== null) {
      const kickZ = soccerZToChartZ(marker.kickZ);
      const kickX = marker.kickX;
      const nearest = candidates
        .map((c) => ({ c, d2: (c[0] - kickZ) ** 2 + (c[1] - kickX) ** 2 }))
        .sort((a, b) => a.d2 - b.d2)[0];
      if (nearest) return nearest.c;
    }

    return candidates[0] ?? null;
  };

  const points = useMemo(() => displayData.map((m) => {
    const x = mode === 'landing' ? (m.landingX ?? m.kickX) : m.kickX;
    const rawZ = mode === 'landing' ? (m.landingZ ?? m.kickZ) : m.kickZ;
    if (x === null || rawZ === null) return null;
    // Flip Z once at the adapter boundary so every downstream consumer (scatter, pulse, glow,
    // hit-test, trajectory) stays in a single coordinate frame.
    return { id: m.id, x, z: soccerZToChartZ(rawZ), m };
  }).filter((p): p is { id: string; x: number; z: number; m: MeasurementMarkerDto } => p !== null), [displayData, mode]);

  // Render reachable regions only when the loaded set contains only Corner/ThrowIn kinds
  // (or a mix of both). Use loaded rows instead of plotted points so Corner/ThrowIn sessions
  // without kick coordinates still render their reachable-region overlays.
  const visibleKinds = useMemo(() => {
    const kinds = new Set(displayData.map((m) => String(m.kind ?? '').trim().toLowerCase()));
    return kinds;
  }, [displayData]);

  const showTopReachableRegions = visibleKinds.size > 0
    && Array.from(visibleKinds).every((kind) => kind === 'corner' || kind === 'throwin');

  const topReachableSegments = useMemo(() => {
    if (!showTopReachableRegions) return [];
    return displayData
      .filter((m) => m.reachableRegionStart && m.reachableRegionEnd)
      .map((m) => {
        const start = toPitchPoint(m.reachableRegionStart as [number, number], m);
        const end = toPitchPoint(m.reachableRegionEnd as [number, number], m);
        if (!start || !end) return null;
        return { id: m.id, onTarget: m.onTarget, start, end };
      })
      .filter((s): s is { id: string; onTarget: number | null; start: [number, number]; end: [number, number] } => s !== null);
  }, [displayData, showTopReachableRegions]);

  const dragSelectablePoints = useMemo(() => {
    const base = points.map((p) => ({ id: p.id, z: p.z, x: p.x }));
    if (topReachableSegments.length === 0) return base;

    const reachableSamples: { id: string; z: number; x: number }[] = [];
    for (const seg of topReachableSegments) {
      const dz = seg.end[0] - seg.start[0];
      const dx = seg.end[1] - seg.start[1];
      const len = Math.hypot(dz, dx);
      const nz = len > 1e-6 ? -dx / len : 0;
      const nx = len > 1e-6 ? dz / len : 0;
      const offsets = [-0.35, 0, 0.35];
      const steps = 8;
      for (let i = 0; i <= steps; i++) {
        const tt = i / steps;
        const z = seg.start[0] + dz * tt;
        const x = seg.start[1] + dx * tt;
        for (const off of offsets) {
          reachableSamples.push({ id: seg.id, z: z + nz * off, x: x + nx * off });
        }
      }
    }
    return [...base, ...reachableSamples];
  }, [points, topReachableSegments]);

  const drag = useDragRectSelect({
    points: dragSelectablePoints,
    getId: (p) => p.id,
    getPixel: (p, instance) => instance.convertToPixel({ gridIndex: 0 }, landscape ? [p.x, p.z] : [p.z, p.x]) as [number, number] | null,
    containerRef,
    echartsRef,
    onPointerHover: onPointHover,
  });

  const markedIds = useMemo(() => new Set(drag.pending?.ids ?? []), [drag.pending]);

  const showEmptyState = data.length === 0 && !clearKicks;

  const isMarked = (id: string) => (showSelectionState && selectedIds.has(id)) || markedIds.has(id);
  const markedBorder = 'rgba(56,189,248,0.95)';

  const markerColor = (measurement: MeasurementMarkerDto): string => {
    return kickMarkerColor(measurement);
  };

  const goals = points.filter((p) => measurementResultBucket(p.m) === 'goal');
  const onTargets = points.filter((p) => measurementResultBucket(p.m) === 'onTarget');
  const offTargets = points.filter((p) => measurementResultBucket(p.m) === 'offTarget');
  const onTargetLegendLabel = useMemo(() => {
    if (onTargets.length === 0) return t('sessions.legendOnTarget');
    const kinds = new Set(onTargets.map((p) => (p.m.result ?? '').toLowerCase()));
    const onlySaved = kinds.size === 1 && kinds.has('saved');
    const onlySavable = kinds.size === 1 && kinds.has('savable');
    const allSavedLike = kinds.size > 0 && Array.from(kinds).every((v) => v === 'saved' || v === 'savable');
    if (onlySaved || allSavedLike) return t('sessions.kickResult_Saved');
    if (onlySavable) return t('sessions.kickResult_Savable');
    return t('sessions.legendOnTarget');
  }, [onTargets]);

  // Build unified pitch base using configurable top-view builder
  const pitchBaseOption = buildTopViewPitchOption({
    // The pitch itself ends at halfway (52.5m); the axis may reach further in landscape, which is plain grass.
    camera: { ...CAMERA_GOAL_AREA_FOCUS, xMin: -viewZ, xMax: viewZ, yMin: viewXMin, yMax: landscape ? FULL_PITCH_LENGTH : viewXMax },
    showFarHalf: landscape,
    showStripes: true,
    showPenaltyBox: true,
    showSixYardBox: true,
    showCenterCircle: true,
    showGoalNet: true,
    showGoal: true,
    showPenaltySpot: true,
    showPenaltyArc: true,
    showTouchlines: true,
    grassColor: PITCH_GREEN,
    zIndex: 0,
  });

  const baseSeries: any[] = pitchBaseOption.series as any[];

  // Faster + tighter stagger now (rounds 1-5 used 18/350; this feels less fidgety on big batches).
  const animateScatter = {
    animation: true,
    animationDuration: 220,
    animationEasing: 'cubicOut' as const,
    animationDelay: (dataIndex: number) => dataIndex * 4,
    animationDurationUpdate: 160,
    animationEasingUpdate: 'cubicOut' as const,
  };

  /** Extracts the trailing string ID from a scatter data tuple. Returns null for non-scatter callbacks
   *  (markLine endpoints, markArea coords, etc.) so the style callbacks can safely fall back to defaults. */
  const idFrom = (params: any): string | null => {
    const d = params?.data;
    if (!Array.isArray(d) || d.length < 3) return null;
    const id = d[2];
    return typeof id === 'string' ? id : null;
  };
  const sizeOf = (params: any) => {
    const id = idFrom(params);
    return id && isMarked(id) ? PITCH_SYMBOL_SIZE + 6 : PITCH_SYMBOL_SIZE;
  };

  const dotStyle = (fallbackColor: string) => ({
    color: (params: any) => withAlpha(params?.data?.[3] ?? fallbackColor, DOT_FILL_ALPHA),
    borderColor: (params: any) => {
      const id = idFrom(params);
      return id && isMarked(id) ? markedBorder : DOT_RING;
    },
    borderWidth: (params: any) => {
      const id = idFrom(params);
      return id && isMarked(id) ? 2.5 : 1.5;
    },
    shadowBlur: 4,
    shadowColor: DOT_HALO,
  });

  const scatterSeries = [
    {
      type: 'scatter',
      name: t('sessions.legendOther'),
      data: offTargets.map((p) => [p.z, p.x, p.id, markerColor(p.m)]),
      symbolSize: sizeOf,
      itemStyle: dotStyle(KICK_RESULT_BUCKET_COLORS.offTarget),
      z: KICK_RESULT_Z.offTarget,
      ...animateScatter,
    },
    {
      type: 'scatter',
      name: onTargetLegendLabel,
      data: onTargets.map((p) => [p.z, p.x, p.id, markerColor(p.m)]),
      symbolSize: sizeOf,
      itemStyle: dotStyle(KICK_RESULT_BUCKET_COLORS.onTarget),
      z: KICK_RESULT_Z.onTarget,
      ...animateScatter,
    },
    {
      type: 'scatter',
      name: t('sessions.legendGoal'),
      data: goals.map((p) => [p.z, p.x, p.id, markerColor(p.m)]),
      symbolSize: sizeOf,
      itemStyle: dotStyle(KICK_RESULT_BUCKET_COLORS.goal),
      z: KICK_RESULT_Z.goal,
      ...animateScatter,
    },
  ];

  const pulsePointsById = useMemo(() => {
    const byId = new Map<string, [number, number, string]>();

    for (const p of points) {
      byId.set(p.id, [p.z, p.x, p.id]);
    }

    // Top-view reachable regions can be visible even when the kick point is not plotted
    // for the current mode/filter. Use segment midpoint as pulse anchor fallback.
    for (const seg of topReachableSegments) {
      if (byId.has(seg.id)) continue;
      const midZ = (seg.start[0] + seg.end[0]) / 2;
      const midX = (seg.start[1] + seg.end[1]) / 2;
      byId.set(seg.id, [midZ, midX, seg.id]);
    }

    return byId;
  }, [points, topReachableSegments]);

  // ALWAYS include the pulse series — when nothing is marked, the data is empty so ECharts renders
  // nothing. Keeping a stable series slot means the previous animation gets diff'd away on Add/Discard
  // rather than left dangling.
  const pulseSeries = [{
    type: 'effectScatter',
    name: 'drag-pulse',
    rippleEffect: { period: 1.4, scale: 3, brushType: 'stroke' as const },
    showEffectOn: 'render' as const,
    data: dragSelectionEnabled && markedIds.size > 0
      ? Array.from(markedIds)
        .map((id) => pulsePointsById.get(id))
        .filter((p): p is [number, number, string] => Boolean(p))
      : [],
    symbolSize: PITCH_SYMBOL_SIZE + 6,
    itemStyle: { color: 'rgba(56,189,248,0.95)' },
    z: 6,
    silent: true,
  }];

  // Static glow overlay for persistently-selected kicks. Using a dedicated series with static
  // itemStyle is more reliable than per-item shadowBlur callbacks (which ECharts honours
  // inconsistently across data shapes).
  const selectedGlowSeries = [{
    type: 'scatter',
    name: 'selected-glow',
    data: showSelectionState && selectedIds.size > 0
      ? points
        .filter((p) => selectedIds.has(p.id))
        .sort((a, b) => a.id.localeCompare(b.id))
        .map((p) => ({ id: p.id, value: [p.z, p.x, p.id] }))
      : [],
    symbolSize: PITCH_SYMBOL_SIZE + 3,
    itemStyle: {
      color: 'rgba(249,115,22,0)',
      borderColor: 'rgb(249,115,22)',
      borderWidth: 2,
    },
    animationDurationUpdate: 120,
    animationEasingUpdate: 'linear',
    z: 5,
    silent: true,
  }];

  // Hover highlight is isolated to its own non-animated series so pointer movement does not
  // force style recalculation/animation across all scatter points.
  const hoveredSeries = [{
    type: 'scatter',
    name: 'hover-glow',
    data: hoveredId
      ? points
        .filter((p) => p.id === hoveredId)
        .map((p) => ({ id: p.id, value: [p.z, p.x, p.id] }))
      : [],
    symbolSize: PITCH_SYMBOL_SIZE + 6,
    itemStyle: {
      color: 'rgba(56,189,248,0)',
      borderColor: 'rgba(56,189,248,0.95)',
      borderWidth: 3,
    },
    animation: false,
    z: 6,
    silent: true,
  }];

  // Reachable-region color scales from white->green by OnTarget score (0..1).
  // NOTE: duplicated with SessionCardPreview.tsx (reachableColor) — differ in start colour and alpha, so not merged.
  const onTargetColor = (value: number | null | undefined): string => {
    const v = Math.max(0, Math.min(1, value ?? 0));
    const r = Math.round(255 + (34 - 255) * v);
    const g = Math.round(255 + (197 - 255) * v);
    const b = Math.round(255 + (94 - 255) * v);
    return `rgba(${r},${g},${b},0.9)`;
  };

  const highlightedReachable = topReachableSegments.filter((s) => hoveredId === s.id || isMarked(s.id));

  const topReachableBorderSeries = highlightedReachable.map((s, idx) => ({
    type: 'line',
    name: `reachable-border-${idx}`,
    data: [
      [s.start[0], s.start[1], s.id],
      [s.end[0], s.end[1], s.id],
    ],
    symbol: 'none',
    lineStyle: {
      color: 'rgba(56,189,248,0.95)',
      width: 11,
      type: 'solid' as const,
      cap: 'square' as const,
      join: 'bevel' as const,
      opacity: 0.98,
    },
    silent: false,
    animation: false,
    z: 3,
  }));

  const topReachableSeries = topReachableSegments.map((s, idx) => ({
    type: 'line',
    name: `reachable-${idx}`,
    data: [
      [s.start[0], s.start[1], s.id],
      [s.end[0], s.end[1], s.id],
    ],
    symbol: 'none',
    lineStyle: {
      color: onTargetColor(s.onTarget),
      width: 8,
      type: 'solid' as const,
      cap: 'square' as const,
      join: 'bevel' as const,
      opacity: 0.78,
    },
    emphasis: {
      lineStyle: {
        opacity: 0.9,
      },
    },
    triggerLineEvent: true,
    silent: false,
    animation: false,
    z: 4,
  }));

  const trajectoryFor: MeasurementMarkerDto[] = [
    ...displayData.filter((m) => showSelectionState && selectedIds.has(m.id) && m.trajectorySamples && m.trajectorySamples.length > 1),
    ...(hoveredId && !(showSelectionState && selectedIds.has(hoveredId))
      ? displayData.filter((m) => m.id === hoveredId && m.trajectorySamples && m.trajectorySamples.length > 1)
      : []),
  ];
  const trajectorySeries = trajectoryFor.map((m, idx) => ({
    type: 'line',
    name: `trajectory-${idx}`,
    // Trajectory samples are [x, z] in soccer coords — flip Z so they sit on the same side as the dots.
    data: (m.trajectorySamples as number[][]).map((p) => [soccerZToChartZ(p[1]), p[0]]),
    symbol: 'none',
    lineStyle: { color: 'rgba(255,255,255,0.85)', width: 1.5, type: hoveredId === m.id ? 'solid' as const : 'dashed' as const },
    silent: true,
    animation: false,
    z: 3,
  }));

  const targetSeries = useMemo(() => buildTargetSeries(targets, soccerZToChartZ, landscape), [targets, landscape]);
  const series = [...baseSeries, ...targetSeries, ...topReachableBorderSeries, ...topReachableSeries, ...trajectorySeries, ...selectedGlowSeries, ...hoveredSeries, ...scatterSeries, ...pulseSeries];

  const portraitOption = {
    backgroundColor: PITCH_GREEN,
    // Round 17: ECharts tooltip is permanently disabled — the new KickInfoModal is the single
    // info surface and the parent drives it from `mouseover` events (see onEvents below).
    tooltip: { show: false },
    legend: { show: false },
    // Zero margins on all sides so the plot area's aspect ratio always matches the container's
    // aspect-ratio CSS (see sessions-kick-locations-view--top in Sessions.scss) — any nonzero,
    // unequal margin here would distort the pitch/goal/box proportions as the window resizes.
    grid: { left: 0, right: 0, top: 0, bottom: 0 },
    xAxis: {
      type: 'value', min: -viewZ, max: viewZ,
      axisLine: { show: false }, axisTick: { show: false }, splitLine: { show: false }, axisLabel: { show: false },
    },
    yAxis: {
      type: 'value', min: viewXMin, max: viewXMax, inverse: true,
      axisLine: { show: false }, axisTick: { show: false }, splitLine: { show: false }, axisLabel: { show: false },
    },
    series,
  };
  const option = landscape ? transposeTopViewOption(portraitOption) : portraitOption;

  const onEvents = {
    extractId: (params: any): string | null => {
      const raw = params?.data;
      if (Array.isArray(raw) && typeof raw[2] === 'string') return raw[2];
      if (raw && Array.isArray(raw.value) && typeof raw.value[2] === 'string') return raw.value[2];
      // Line-body hover (triggerLineEvent): params.data is null; fall back to series name.
      if (typeof params?.seriesName === 'string') {
        const match = /^reachable(?:-border)?-(\d+)$/.exec(params.seriesName);
        if (match) {
          const seg = topReachableSegments[parseInt(match[1], 10)];
          if (seg) return seg.id;
        }
      }
      return null;
    },
    click: (params: any) => {
      if (drag.pending) return;
      const id = onEvents.extractId(params);
      if (!id) return;
      // Use the underlying browser MouseEvent for accurate screen coords.
      const ev = params.event?.event ?? params.event;
      const clientX = typeof ev?.clientX === 'number' ? ev.clientX : 0;
      const clientY = typeof ev?.clientY === 'number' ? ev.clientY : 0;
      onPointClick(id, clientX, clientY);
    },
    mouseover: (params: any) => {
      if (drag.isDragging || drag.pending) return;
      const id = onEvents.extractId(params);
      if (!id) return;
      const ev = params.event?.event ?? params.event;
      let clientX = typeof ev?.clientX === 'number' ? ev.clientX : undefined;
      let clientY = typeof ev?.clientY === 'number' ? ev.clientY : undefined;

      // Line-series events (reachable regions) can come without browser client coords.
      // Fall back to ECharts canvas offsets so hover opens the same modal as scatter points.
      if ((clientX === undefined || clientY === undefined) && typeof params.event?.offsetX === 'number' && typeof params.event?.offsetY === 'number') {
        const bounds = containerRef.current?.getBoundingClientRect();
        if (bounds) {
          clientX = bounds.left + params.event.offsetX;
          clientY = bounds.top + params.event.offsetY;
        }
      }

      if ((clientX === undefined || clientY === undefined) && typeof params.event?.zrX === 'number' && typeof params.event?.zrY === 'number') {
        const bounds = containerRef.current?.getBoundingClientRect();
        if (bounds) {
          clientX = bounds.left + params.event.zrX;
          clientY = bounds.top + params.event.zrY;
        }
      }

      // Final fallback: use the last tracked DOM mouse position (always reliable).
      if ((clientX === undefined || clientY === undefined) && lastMouseClient.current) {
        clientX = lastMouseClient.current.clientX;
        clientY = lastMouseClient.current.clientY;
      }

      onPointHover(id, clientX, clientY);
    },
    // Don't auto-close the kick modal on globalout — the user dismisses with ×, click-outside, or Esc.
    // But DO clear cross-chart hover linking.
    globalout: () => onPointHover(null),
  };

  // performanceMode keeps its original blank-state behaviour (no future Sessions consumer regression).
  if (showEmptyState && performanceMode) {
    return <div className='sessions-empty-state' />;
  }

  // Merge mouse-position tracking with the drag handler so both run on every move.
  const { onMouseMove: dragMouseMove, ...restDragHandlers } = drag.handlers;
  const combinedMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    lastMouseClient.current = { clientX: e.clientX, clientY: e.clientY };
    if (dragSelectionEnabled) dragMouseMove(e);
  };

  return (
    <div ref={containerRef} className='sessions-pitch-chart-wrapper sessions-pitch-chart-wrapper--top' onMouseMove={combinedMouseMove} {...(dragSelectionEnabled ? restDragHandlers : {})}>
      <ReactECharts
        ref={echartsRef}
        option={option}
        style={{ width: '100%', height: '100%' }}
        onEvents={onEvents}
        notMerge
      />
      {showEmptyState && !performanceMode && (
        <div className='sessions-goal-empty-overlay'>
          <p className='chart-empty'>{t('sessions.chartEmpty')}</p>
          {hasActiveFilters && onClearFilters && (
            <button
              type='button'
              className='sessions-reset-btn'
              onClick={onClearFilters}
            >
              {t('sessions.clearFiltersEmptyState')}
            </button>
          )}
        </div>
      )}
      {dragSelectionEnabled && drag.dragRect && (
        <div className='sessions-pitch-drag-rect' style={drag.dragRect} />
      )}
      {dragSelectionEnabled && drag.pending && (
        <div
          className='sessions-pitch-pending-modal'
          style={{ left: clampModalX(drag.pending.x, containerRef.current?.clientWidth), top: clampModalY(drag.pending.y, containerRef.current?.clientHeight) }}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseUp={(e) => e.stopPropagation()}
        >
          <button type='button' className='sessions-pitch-pending-add' onClick={() => { onDragSelected(drag.pending!.ids); drag.clearPending(); }}>
            {t('sessions.dragSelectAdd', { count: drag.pending.ids.length })}
          </button>
          <button type='button' className='sessions-pitch-pending-discard' onClick={drag.clearPending}>
            {t('sessions.dragSelectDiscard')}
          </button>
        </div>
      )}
    </div>
  );
};

export default SessionsPitchChart;
